// NPC dialog in the browser (qa-runs#1167): the lines this person heard (the DM's every line, with who heard it), the
// bubbles showing over tokens now, and the DM's requests over `table:dialog`. The lines come from the server alone —
// a load of the sidebar (`list`) and each `dialog` frame — so a line a player did not hear is never here to be found.
// One store for the page: the Dialogue panel and the board's bubbles read it.
import { useSyncExternalStore } from 'react';
import type { Socket } from 'socket.io-client';
import { bubbleMs, dmDialogLine, isDialogBody, type DmDialogLine, type PlayerDialogLine, type ProjectedEvent } from '@dragons-table/vtt-shared';

export const TABLE_DIALOG = 'table:dialog';
export type Line = PlayerDialogLine | DmDialogLine;
export interface Bubble { line: Line; until: number }
export interface DialogView { lines: Line[]; bubbles: Record<string, Bubble>; loaded: boolean; speaker: string | null }
export type DialogReply = { ok: true; lines?: Line[]; id?: string } | { ok: false; error: string; reason?: string };

const KEEP = 1000; // lines held in the sidebar; older ones are read again on the next load

let view: DialogView = { lines: [], bubbles: {}, loaded: false, speaker: null };
let socket: Socket | null = null;
let campaignId = '';
const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const set = (next: Partial<DialogView>) => { view = { ...view, ...next }; for (const fn of listeners) fn(); };

export const dialogs = {
  get: () => view,
  subscribe(fn: () => void) { listeners.add(fn); return () => { listeners.delete(fn); }; },
  /** The table this page sits at: its lines are read now, and again after a resync. */
  attach(s: Socket, id: string) {
    if (socket !== s || campaignId !== id) { for (const t of timers.values()) clearTimeout(t); timers.clear(); set({ lines: [], bubbles: {}, loaded: false }); }
    socket = s; campaignId = id;
    void dialogs.reload();
  },
  async reload(): Promise<void> {
    const r = await request({ op: 'list' });
    if (r.ok && r.lines) set({ lines: r.lines.slice(-KEEP), loaded: true });
  },
  /** A frame this person was sent: a line they heard (its bubble shown), a line deleted, or the DM's own event. */
  take(ev: ProjectedEvent) {
    let line: Line | null = null;
    if (ev.kind === 'dialog') line = ev.line;
    else if (ev.kind === 'dialog.gone') return drop(ev.id);
    else if (ev.kind === 'dm.event' && isDialogBody(ev.body)) {
      if (ev.body.kind === 'dialog.delete') return drop(ev.body.id);
      line = dmDialogLine(ev.body.line, ev.at);
    }
    if (!line) return;
    const lines = [...view.lines.filter((l) => l.id !== line!.id), line].slice(-KEEP);
    const bubbles = { ...view.bubbles };
    if (line.objectId) {
      // Each token shows its newest line.
      const key = line.objectId;
      bubbles[key] = { line, until: Date.now() + bubbleMs(line.text) };
      clearTimeout(timers.get(key));
      timers.set(key, setTimeout(() => dismiss(key, line!.id), bubbleMs(line.text)));
    }
    set({ lines, bubbles });
  },
  /** Close a bubble now (a click on it). */
  dismiss: (objectId: string) => dismiss(objectId),
  /** The speaker the Dialogue panel opens with (the token menu's Say…). */
  pickSpeaker: (id: string | null) => set({ speaker: id }),
  say: (body: { text: string; speaker?: string; name?: string; to?: string[] }) => request({ op: 'say', ...body }),
  remove: (id: string) => request({ op: 'delete', id }),
};

function dismiss(objectId: string, onlyId?: string) {
  const b = view.bubbles[objectId];
  if (!b || (onlyId && b.line.id !== onlyId)) return;
  const bubbles = { ...view.bubbles };
  delete bubbles[objectId];
  clearTimeout(timers.get(objectId)); timers.delete(objectId);
  set({ bubbles });
}

function drop(id: string) {
  const bubbles = Object.fromEntries(Object.entries(view.bubbles).filter(([, b]) => b.line.id !== id));
  set({ lines: view.lines.filter((l) => l.id !== id), bubbles });
}

function request(body: Record<string, unknown>): Promise<DialogReply> {
  const s = socket;
  if (!s || !s.connected) return Promise.resolve({ ok: false, error: 'refused', reason: 'You are disconnected; nothing you do now is sent.' });
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false, error: 'refused', reason: 'That did not reach the table in time. Try again.' }), 10_000);
    s.emit(TABLE_DIALOG, { campaignId, ...body }, (r: DialogReply) => { clearTimeout(timer); resolve(r ?? { ok: false, error: 'refused' }); });
  });
}

export function useDialog(): DialogView {
  return useSyncExternalStore(dialogs.subscribe, dialogs.get, dialogs.get);
}

/** "to you", "to Mary and Joe", "to everyone on the scene" — the DM's line of who heard it. */
export const heardWords = (l: Line, nameOf: (id: string) => string): string | null => {
  if (!('heard' in l)) return null;
  if (!l.heard.length) return 'Heard by nobody';
  return `Heard by ${l.heard.map((h) => nameOf(h.id)).join(', ')}`;
};
